import cx from 'classnames'
import { MarketplaceIcon } from 'icons'

export type PressCard = {
  title: string
  body: string
  articleUrl: string
  logoImage: { url: string } | null
  logoImageDimensions: { width: number; height: number } | null
}

type LogoTreatment = {
  heightClass: string
  opacityClass: string
  hoverOpacityClass: string
}

type PressTreatment = {
  wordmark?: React.FunctionComponent<React.SVGAttributes<SVGElement>>
  byline?: string
  logo?: LogoTreatment
}

// Supporting logos are desaturated, then knocked back per logo so both read at
// equal weight. GrepBeat's values double as the default for any publication the
// design does not call out.
const DEFAULT_LOGO: LogoTreatment = {
  heightClass: 'h-24',
  opacityClass: 'opacity-70',
  hoverOpacityClass: 'group-hover:opacity-100',
}

// The design pins three things to named publications: Marketplace's inline
// wordmark and its "Public Radio" byline, and how far a supporting logo is
// knocked back. Contentful has no field for any of them and ACR-29 does not add
// one, so they hang off the publication title instead. Titles that are absent
// here fall back to the CMS logo, no byline, and DEFAULT_LOGO.
const PRESS_TREATMENTS: Record<string, PressTreatment> = {
  Marketplace: { wordmark: MarketplaceIcon, byline: 'Public Radio' },
  'The News & Observer': {
    logo: {
      heightClass: 'h-[26px]',
      opacityClass: 'opacity-[.55]',
      hoverOpacityClass: 'group-hover:opacity-[.85]',
    },
  },
}

const treatmentFor = (title: string): PressTreatment =>
  PRESS_TREATMENTS[title.trim()] ?? {}

// One text node. Handing React the quote marks as separate children makes it
// emit them as separate nodes (with `<!-- -->` separators in the SSR output),
// which breaks text shaping across the quote marks.
const quoted = (body: string) => `“${body}”`

const ITEM_CLASSES =
  'group flex flex-col items-center text-center focus-visible:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-teal-4'

const SOURCE_CLASSES =
  'font-semibold text-teal-5 underline underline-offset-[3px] transition-colors duration-[180ms] ease-[ease] group-hover:text-teal-3 group-focus-visible:text-teal-3'

const LOGO_CLASSES =
  'block w-auto grayscale transition-opacity duration-[180ms] ease-[ease]'

const PressItemLink = (props: {
  card: PressCard
  className?: string
  children: React.ReactNode
}) => (
  <a
    className={cx(ITEM_CLASSES, props.className)}
    href={props.card.articleUrl}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={props.card.title}
  >
    {props.children}
  </a>
)

// The design sizes every logo by height and lets width follow the asset's own
// ratio. next/image v12 demands both dimensions and writes inline styles that
// beat these classes, so Contentful logos render as plain images. The CMS
// dimensions stay on the element as the pre-load aspect-ratio hint only.
const PressLogo = (props: { card: PressCard; className: string }) =>
  props.card.logoImage ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={props.card.logoImage.url}
      alt={props.card.title}
      width={props.card.logoImageDimensions?.width}
      height={props.card.logoImageDimensions?.height}
      className={props.className}
    />
  ) : null

const LeadItem = (props: { card: PressCard }) => {
  const { byline, wordmark: Wordmark } = treatmentFor(props.card.title)
  const markClasses = 'block h-[44px] w-auto md-d:h-[36px]'

  return (
    <PressItemLink card={props.card}>
      {Wordmark ? (
        <Wordmark className={markClasses} />
      ) : (
        <PressLogo card={props.card} className={markClasses} />
      )}
      <blockquote className="mt-32 max-w-800 text-pretty text-24 leading-8 md-d:mt-24 md-d:text-18 md-d:leading-7">
        {quoted(props.card.body)}
      </blockquote>
      <p className="mt-32 text-16 leading-6 md-d:mt-24">
        <span className={SOURCE_CLASSES}>{props.card.title}</span>
        {byline ? (
          <span className="mt-4 block text-grey-6">{byline}</span>
        ) : null}
      </p>
    </PressItemLink>
  )
}

const SupportingItem = (props: { card: PressCard; hasDivider: boolean }) => {
  const { heightClass, opacityClass, hoverOpacityClass } =
    treatmentFor(props.card.title).logo ?? DEFAULT_LOGO

  return (
    <PressItemLink
      card={props.card}
      className={cx('flex-1 px-40 lg-d:px-0', {
        // The design's `.item + .item` rule: a rule between the columns above
        // 1024, and between the stacked rows below it.
        'border-l border-grey-3 lg-d:mt-40 lg-d:border-l-0 lg-d:border-t lg-d:pt-40':
          props.hasDivider,
      })}
    >
      <span className="flex h-[28px] items-center">
        <PressLogo
          card={props.card}
          className={`${LOGO_CLASSES} ${heightClass} ${opacityClass} ${hoverOpacityClass}`}
        />
      </span>
      <blockquote className="mt-24 max-w-420 text-pretty text-16 leading-6 text-grey-7">
        {quoted(props.card.body)}
      </blockquote>
      <p className="mt-auto pt-24 text-16 leading-6">
        <span className={SOURCE_CLASSES}>{props.card.title}</span>
      </p>
    </PressItemLink>
  )
}

// The array can contain holes: Contentful returns null in place of an
// individual unpublished card, and PageBlocks types this prop as Array<any>, so
// nothing upstream is guaranteed to have removed them. The type says so.
const PressCards = (props: { cards: Array<PressCard | null> }) => {
  // Holes are dropped before the split, not skipped during the render: a null
  // in a supporting slot throws on `.title`, and a null in first position would
  // take the whole section down with it while valid cards remain.
  const published = props.cards.filter(
    (card): card is PressCard => card != null
  )

  // The first published entry leads; the rest are supporting coverage.
  // Ordering is a CMS concern, so nothing here reads a `featured` flag.
  const [lead, ...supporting] = published

  if (!lead) return null

  // text-grey-8 is load-bearing, not a repeat of the body default: the website
  // layout's <main> sets text-web-darkest, so the quote copy would otherwise
  // inherit #070707 instead of the design's #2D2D2D.
  return (
    <section className="rounded bg-white px-16 py-40 text-grey-8 sm:p-48">
      <h2 className="mb-40 text-center text-16 font-semibold leading-6 text-teal-3 md-d:mb-32">
        In the press
      </h2>

      <LeadItem card={lead} />

      {supporting.length > 0 ? (
        <>
          <hr className="my-40 h-px border-0 bg-grey-3" />
          <div className="flex lg-d:flex-col">
            {supporting.map((card, idx) => (
              <SupportingItem key={idx} card={card} hasDivider={idx > 0} />
            ))}
          </div>
        </>
      ) : null}
    </section>
  )
}

export default PressCards
